// no itx for this file
import universalstorageHandler from "../persistence/UniversalstorageHandler";
import BaseModel from "./BaseModel";
import { ApplicationMode } from "./SettingsEnums";

export enum ExportState {
  None,
  WaitingForUploadReady,
  WaitingForConversion,
  ConversionIsRunning,
  WaitingForDownload,
  Failure,
}

export class UserReference {
  static fromUser(user: any): UserReference {
    const res = new UserReference();
    res.id = user.id;
    res.name = user.name;
    res.email = user.email;
    res.status = user.status ?? "";
    return res;
  }

  id: string = "";
  name: string = "";
  email: string = "";
  status: string = "";
}

const VERSION = 4;

// Older autobackups appended their type and timestamp to the actual project
// description. Remove only those generated suffixes, leaving normal text intact.
export function removeLegacyAutobackupDescriptionSuffix(description: unknown): string {
  if (typeof description !== "string") return "";

  const legacyAutobackupSuffix =
    /(?:(?:\s|&#x20;|&#32;|&nbsp;)*Autobackup\s+\d{1,2}[./]\d{1,2}[./]\d{2,4},?\s+\d{1,2}:\d{2}(?::\d{2})?)+(?:\s|&#x20;|&#32;|&nbsp;)*$/iu;
  return description.replace(legacyAutobackupSuffix, "").trim();
}

// VERSION 3;
// mwe 16.12.2024
//  Device physicalAddress => _physicalAddress
//
// VERSION 4;
// fg 21.05.2026
//  Device idDali new if changingbetween different versions  app and released( old) version is dali will be missing
//

import type { ProjectOfferRequest } from "./ProjectOfferRequest";

export default class ProjectMetadata extends BaseModel {
  public get className(): string {
    return "ProjectMetadata";
  }

  constructor() {
    super();
  }

  override getPersistentProperties(): string[] {
    return ["isC1Project", ...super.getPersistentProperties()];
  }

  offerRequests: ProjectOfferRequest[] = [];

  onlineMetadata?: ProjectMetadata;

  users = new Array<UserReference>();
  /** Historical users from a local restore must be refreshed before a cloud write. */
  projectUsersRefreshRequired = false;

  version = 1;
  description = "";
  etsId = "";
  etsGuid = "";
  etsPin = "";
  author = "";

  gptThreadId: string | undefined;
  gptAssistantId: string | undefined;
  gptToRunText: string | undefined;

  created: Date = new Date();
  private _lastModified: Date = new Date();

  set lastModifiedOnServer(newDate: Date) {
    this._lastModified = newDate;
  }
  get lastModifiedOnServer(): Date {
    return this._lastModified;
  }

  //
  // The moment where the local Version is copied/loaded from the server
  //
  localCopied: Date | undefined;
  lastModifiedLocal: Date | undefined;

  get localCopiedTime(): number {
    return this.localCopied?.getTime() || 0;
  }

  loadedFrom: "local" | "server" | undefined;

  /////////////////////////////////////////////////////////////////////
  // for example project
  readOnly: boolean = false;
  /////////////////////////////////////////////////////////////////////

  ////////////////////////////////////////////////////////////////////
  // snapchots
  snapshots: Array<ProjectMetadata> = [];
  ////////////////////////////////////////////////////////////////////

  /////////////
  // extra information for project handling
  isExampleProject: boolean = false;
  isCreatedWithAssistent: boolean = false;
  isC1Project: boolean = false;
  isEstadaProject: boolean = true;
  applicationMode: ApplicationMode = ApplicationMode.Main;
  ////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////////////////
  // ETS Sync statistics

  exportedToEtsViaServer = 0;
  syncedToEtsViaPlugin = 0;

  uploadedToServerForConversion: Date | undefined = undefined;
  exportState: ExportState = ExportState.None;
  etsLog = [];
  opendDateTime = new Date();

  uniqueTagsForProject = true;
  priceListOneCabinetOnePage = false;
  showCablePullListCableInfos = false;
  defaultNumberOfWiresPerCable = 5;
  userBackgroundColor: string | undefined = "var(--app-background)";
  userTextColor: string | undefined = undefined;

  importedFromEts?: boolean;

  statistic = {
    numberOfActors: 0,
    numberOfButtons: 0,
    numberOfSensors: 0,
    numberOfDecentralActors: 0,
    actorTemplates: [] as Array<string>,
    buttonTemplates: [] as Array<string>,
    sensorTemplates: [] as Array<string>,
    decentralActorTemplates: [] as Array<string>,
    numberOfRooms: 0,
    numberOfFuses: 0,
    numberOfTerminals: 0,
    numberOfGroupAdresses: 0,
    numberOfGroupAdressesGeneratings: 0,
    numberOfConnections: 0,
    numberOfScenes: 0,
    hoursInEdit: 0,
    numberOfEtsExports: 0,
    numberOfEtsSyncs: 0,
    numberOfBackgoundImages: 0,
    numberOfProgrammedDevices: 0,
    numberOfProgrammingProcesses: 0,
    templates: [] as Array<string>,
  };
  projectLicence = {
    invoiceIds: [] as Array<string>,
  };

  persistantAssistanceData = {
    projectOpenCount: 0,
    isDemoProject: false,
  };

  assistanceData = {
    projectFirstTimeOpen: (): boolean => {
      return this.persistantAssistanceData.projectOpenCount <= 1;
    },
    isDemoProject: (): boolean => {
      return this.persistantAssistanceData.isDemoProject;
    },
  };

  toJSON = (): any => {
    return {
      id: this.id,
      name: this.name,
      etsId: this.etsId,
      description: this.description,
      created: this.created.toISOString(),
      lastModified: this._lastModified.toISOString(),
      lastModifiedLocal: this.lastModifiedLocal?.toISOString(),
      lastExportedToEts: this.uploadedToServerForConversion?.toISOString(),
      users: this.users,
      projectUsersRefreshRequired: this.projectUsersRefreshRequired,
      exportState: this.exportState,
      // etsLog: this.etsLog,  we dont store this !!! MWE 30.11.2023
      version: VERSION,
      persistantAssistanceDate: this.persistantAssistanceData,
      gptThreadId: this.gptThreadId,
      gptAssistantId: this.gptAssistantId,
      uniqueTagsForTProject: this.uniqueTagsForProject,
      priceListOneCabinetOnePage: this.priceListOneCabinetOnePage,
      showCablePullListCableInfos: this.showCablePullListCableInfos,
      defaultNumberOfWiresPerCable: this.defaultNumberOfWiresPerCable,
      userBackgroundColor: this.userBackgroundColor,
      userTextColor: this.userTextColor,
      localCopied: this.localCopied?.toISOString(),
      statistic: this.statistic,
      projectLicence: this.projectLicence,
      offerRequests: this.offerRequests,
      readOnly: this.readOnly,
      isExampleProject: this.isExampleProject,
      isCreatedWithAssistent: this.isCreatedWithAssistent,
      isC1Project: this.isC1Project,
      isEstadaProject: this.isEstadaProject,
      applicationMode: this.applicationMode,

      etsPin: this.etsPin,
      importedFromEts: this.importedFromEts,
      exportedToEtsViaServer: this.exportedToEtsViaServer,
      syncedToEtsViaPlugin: this.syncedToEtsViaPlugin,
    };
  };

  fromJSON = (data: any): void => {
    this.projectUsersRefreshRequired = data.projectUsersRefreshRequired === true;
    this.id = data.id;
    this.name = data.name;
    this.offerRequests = Array.isArray(data.offerRequests) ? data.offerRequests : [];
    this.etsId = data.etsId;
    this.description = removeLegacyAutobackupDescriptionSuffix(data.description);
    this.created = new Date(data.created);
    this._lastModified = new Date(data.lastModified);
    this.lastModifiedLocal = data.lastModifiedLocal ? new Date(data.lastModifiedLocal) : undefined;
    this.readOnly = data?.readOnly;
    this.isExampleProject = data.isExampleProject;
    this.isCreatedWithAssistent = data.isCreatedWithAssistent;
    this.isC1Project = data.isC1Project === true;
    this.isEstadaProject = data.isEstadaProject === true;
    this.applicationMode =
      data.applicationMode === ApplicationMode.ConceptOne ? ApplicationMode.ConceptOne : ApplicationMode.Main;
    this.etsPin = data.etsPin || "";

    if (data.lastExportedToEts) {
      this.uploadedToServerForConversion = new Date(data.lastExportedToEts);
    }
    this.users = new Array<UserReference>();
    if (data.users) {
      for (let i = 0; i < data.users.length; i++) {
        const u = data.users[i];
        const userRef = UserReference.fromUser(u);
        this.users.push(userRef);
      }
    }
    this.exportState = data.exportState;
    // this.etsLog = data.etsLog ? data.etsLog : [];
    if (data.version) {
      this.version = data.version;
    }
    if (data.persistantAssistanceDate) {
      this.persistantAssistanceData = data.persistantAssistanceDate;
      this.persistantAssistanceData.projectOpenCount++;
    }

    this.gptThreadId = data.gptThreadId;
    this.gptAssistantId = data.gptAssistantId;

    if (data.uniqueTagsForTProject != undefined) {
      this.uniqueTagsForProject = data.uniqueTagsForTProject;
    }
    if (data.priceListOneCabinetOnePage != undefined) {
      this.priceListOneCabinetOnePage = data.priceListOneCabinetOnePage;
    }
    if (data.showCablePullListCableInfos != undefined) {
      this.showCablePullListCableInfos = data.showCablePullListCableInfos;
    }

    if (data.defaultNumberOfWiresPerCable != undefined) {
      this.defaultNumberOfWiresPerCable = data.defaultNumberOfWiresPerCable;
    }
    if (data.userBackgroundColor != undefined) {
      this.userBackgroundColor = data.userBackgroundColor;
    }
    if (data.userTextColor != undefined) {
      this.userTextColor = data.userTextColor;
    }

    this.localCopied = data.localCopied ? new Date(data.localCopied) : undefined;

    if (data.statistic != undefined) {
      this.statistic = data.statistic;
    }
    if (data.projectLicence != undefined) {
      this.projectLicence = data.projectLicence;
    }

    this.loadedFrom = data.loadedFrom;
    this.importedFromEts = data.importedFromEts;

    this.exportedToEtsViaServer = data.exportedToEtsViaServer;
    this.syncedToEtsViaPlugin = data.syncedToEtsViaPlugin;
  };

  setUserForNewProject() {
    this.projectUsersRefreshRequired = false;
    const sUser = localStorage.getItem("user");
    if (sUser && sUser != "") {
      this.ensureUser(JSON.parse(sUser));
    }
  }

  ensureUser(user: { id?: string; name?: string; email?: string; status?: string } | undefined) {
    if (!user) return;
    const id = user.id?.trim();
    const email = user.email?.trim().toLocaleLowerCase();
    const alreadyAssigned = this.users.some(
      (assignedUser) =>
        (id && assignedUser.id === id) || (email && assignedUser.email?.trim().toLocaleLowerCase() === email),
    );
    if (!alreadyAssigned) this.users.push(UserReference.fromUser(user));
  }
  updateNewStatistic() {
    function getDeviceCategoryTemplates(devices: Array<any>): Array<string> {
      const templateMap = new Map<string, { info: any; count: number }>();
      let noTemplateCount = 0;
      for (const device of devices) {
        if (device.template) {
          const id = device.template.id as string;
          if (!templateMap.has(id)) {
            templateMap.set(id, { info: device.template.knxProductData?.productInfo, count: 0 });
          }
          templateMap.get(id)!.count++;
        } else {
          noTemplateCount++;
        }
      }
      const result: Array<string> = [];
      for (const { info, count } of templateMap.values()) {
        result.push(
          [
            info?.ManufacturerName ?? "",
            (info as any)?.ProductName ?? info?.DefaultProductName ?? "",
            info?.OrderNumber ?? "",
            info?.ApplicationVersion ?? "",
            info?.VersionNumber ?? "",
            count,
          ].join("§"),
        );
      }
      if (noTemplateCount > 0) result.push(`without template§${noTemplateCount}`);
      return result;
    }
    let thisTimeDifference = new Date().getTime() - this.opendDateTime.getTime();
    thisTimeDifference = thisTimeDifference / 1000 / 60 / 60; // msec-> sec-> min-> h
    this.opendDateTime = new Date();

    this.statistic = {
      ...this.statistic,
      numberOfActors: state.allActors().filter((a) => a.template).length,
      numberOfButtons: state.allButtons().filter((b) => !b.isSensor && !b.isDecentralActor && b.template).length,
      numberOfSensors: state.allButtons().filter((b) => b.isSensor && b.template).length,
      numberOfDecentralActors: state.allDecentralActors().filter((b) => b.template).length,
      actorTemplates: getDeviceCategoryTemplates(state.allActors()),
      buttonTemplates: getDeviceCategoryTemplates(state.allButtons().filter((b) => !b.isSensor && !b.isDecentralActor)),
      sensorTemplates: getDeviceCategoryTemplates(state.allButtons().filter((b) => b.isSensor)),
      decentralActorTemplates: getDeviceCategoryTemplates(state.allDecentralActors()),
      numberOfRooms: state.allRooms().length,
      numberOfFuses: state.allFuses().length,
      numberOfTerminals: state.allTerminals().length,
      numberOfGroupAdresses: state.allGroupAddresses(true).length,
      numberOfConnections: state.allConnections.getAll().length,
      numberOfScenes: state.allScenes().length,
      hoursInEdit: (this.statistic?.hoursInEdit || 0) + thisTimeDifference,
      numberOfBackgoundImages: state.allPagesWithBackgroundUsages().length,
    };
  }
  updateProjectLicence() {
    const invoiceIds = state.projectLicense?.licenses?.map((l) => l.invoiceId) || [];
    this.projectLicence = {
      ...this.projectLicence,
      invoiceIds: invoiceIds,
    };
  }

  clear() {
    this.projectUsersRefreshRequired = false;
    this.id = "againInProjectSelection";
    this.name = "";
    this.offerRequests = [];
    this.etsId = "";
    this.description = "";
    this.created = new Date(1);
    this.lastModifiedOnServer = new Date(1);
    this.lastModifiedLocal = undefined;
    this.localCopied = undefined;
    this.uploadedToServerForConversion = undefined;
    this.users = new Array<UserReference>();
    this.exportState = 0;
    this.etsLog = [];
    this.isC1Project = false;
    this.isEstadaProject = true;
    this.applicationMode = ApplicationMode.Main;
  }

  get onlineSyncState():
    | "OfflineIsNewerServerIsUnchanged"
    | "SyncNeeded"
    | "Insync"
    | "ServerIsNewerDownloadNeeded"
    | "OfflineNoServerConnection"
    | "online" {
    if (state.onlineOfflineMode.value == "online") {
      return "online";
    } else {
      if (this.onlineMetadata) {
        if (this.lastModifiedOnServer.getTime() == this.onlineMetadata.lastModifiedOnServer.getTime()) {
          return "Insync";
        }

        if (this.lastModifiedOnServer.getTime() > this.onlineMetadata.lastModifiedOnServer.getTime()) {
          if (this.localCopied?.getTime() == this.onlineMetadata.lastModifiedOnServer.getTime()) {
            return "OfflineIsNewerServerIsUnchanged";
          } else return "SyncNeeded";
        }

        return "SyncNeeded";
      } else {
        return "OfflineNoServerConnection";
      }
    }
  }

  //
  //  this function returns false,
  //  if the project is not yet copied local
  //
  //  this function also returns false,
  //    if this projectMetadata is loaded local
  //       and so we dont know the current server state
  //

  checkIfProjectIsModifiedOnServerSinceCopiedLocal(): boolean {
    if (this.loadedFrom == "local") {
      return false;
    }

    if (this.localCopied) {
      return this.localCopied.getTime() < this.lastModifiedOnServer.getTime();
    } else {
      return false;
    }
  }

  //
  //  this function returns false,
  //  if the project is not yet copied local
  //
  checkIfProjectIsModifiedLocal() {
    if (this.lastModifiedLocal && this.localCopied && this.lastModifiedLocal.getTime() > this.localCopied.getTime()) {
      return true;
    } else {
      return false;
    }
  }

  checkIfProjectIsLocalStored() {
    if (this.localCopied) {
      return true;
    } else {
      return false;
    }
  }

  //
  //  this function returns true,
  //   ** if the project is not yet copied local
  //   ** if the project is modified onServer AFTER the localCopied timestamp
  //
  checkIfProjectIsNeedToBeCopiedLocal() {
    if (this.loadedFrom == "local") {
      return false;
    }

    if (!this.localCopied) {
      return true;
    }

    return this.lastModifiedOnServer.getTime() > this.localCopied.getTime();
  }

  async updateFromServer(): Promise<boolean> {
    if (this.isDirty || universalstorageHandler.isSavingProject) return false;
    if (!universalstorageHandler.storageHandler.isLocalStorgeHandle && !universalstorageHandler.temporaryOffline) {
      const projectId = this.id;
      const data = await universalstorageHandler.storageHandler.loadProjectMetadata(projectId);
      if (data) {
        // A local change or project switch may have happened while the request was pending.
        if (this.id !== projectId || this.isDirty || universalstorageHandler.isSavingProject) return false;
        const x_localCopied = this.localCopied;
        const x_lastModifiedLocal = this.lastModifiedLocal;
        const wasC1Project = this.isC1Project;

        this.fromJSON({ ...data, projectUsersRefreshRequired:
          this.projectUsersRefreshRequired && !Array.isArray(data.users) });

        this.loadedFrom = "server";
        this.localCopied = x_localCopied;
        this.lastModifiedLocal = x_lastModifiedLocal;
        if (this.isC1Project !== wasC1Project) this.raiseOnChangeUI("isC1Project");

        return true;
      }
    }

    return false;
  }
}
